import { emberAppBaseUrl } from "utils/envVariables";

import shortSecurity from "./shortSecurity";

export const jsonLDForInsight = ({ insight, canonicalUrl }: any) => {
  const {
    tagline,
    byline,

    account,
    entity,
    contentVertical,

    isPublic,
  } = insight || {};
  const executiveSummaryPlain = insight.executiveSummary;
  const keywords = insight.automaticKeywords;
  const authorName = account?.name;
  const authorSlug = account?.slug;
  const authorTitle = account?.titleText;
  const authorPhoto = account?.avatarUrl?.xlarge;
  const authorCountry = account?.country;
  const entityName = entity?.shortName;
  const entitySlug = entity?.slug;
  const entityDescription = entity?.entityDescription;
  const entityCountry = entity?.country;
  const entityTicker = entity?.uniqueTicker;
  const vertical = contentVertical?.name;
  const verticalSlug = contentVertical?.slug;

  const crumbs = [
    {
      "@type": "ListItem",
      item: { "@id": emberAppBaseUrl, name: "Smartkarma" },
    },
    ...(vertical
      ? [
          {
            "@type": "ListItem",
            item: {
              "@id": `${emberAppBaseUrl}/verticals/${verticalSlug}`,
              name: vertical,
            },
          },
        ]
      : []),
    ...(entity
      ? [
          {
            "@type": "ListItem",
            item: {
              "@id": `${emberAppBaseUrl}/entities/${entitySlug}`,
              name: entityName,
            },
          },
        ]
      : []),
    {
      "@type": "ListItem",
      item: {
        "@id": `${emberAppBaseUrl}/profiles/${authorSlug}`,
        name: authorName,
      },
    },
  ];

  return [
    {
      "@context": "http://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: crumbs.map((item, index) => ({
        ...item,
        position: index,
      })),
    },
    {
      "@context": "http://schema.org/",
      "@type": "Article",
      headline: tagline,
      author: {
        "@context": "http://schema.org/",
        "@type": "Person",
        name: authorName,
        jobTitle: authorTitle,
        image: {
          "@context": "http://schema.org/",
          "@type": "ImageObject",
          url: authorPhoto,
          caption: authorName,
        },
        workLocation: {
          "@context": "http://schema.org/",
          "@type": "Place",
          address: authorCountry,
        },
        sameAs: [],
        url: `${emberAppBaseUrl}/profiles/${authorSlug}`,
      },
      datePublished: insight.publishedAt,
      dateModified: insight.updatedAt,
      publisher: {
        "@context": "http://schema.org/",
        "@type": "Organization",
        name: authorName,
        logo: {
          "@context": "http://schema.org/",
          "@type": "ImageObject",
          url: authorPhoto,
          caption: authorName,
        },
        address: authorCountry,
        sameAs: [],
        url: `${emberAppBaseUrl}/profiles/${authorSlug}`,
        review: null,
      },
      articleBody: (byline || executiveSummaryPlain)?.slice?.(0, 550),
      articleSection: vertical,
      image: {
        "@context": "http://schema.org/",
        "@type": "ImageObject",
        url: authorPhoto,
        caption: tagline,
      },
      mainEntityOfPage: canonicalUrl,
      mainEntity: {
        "@context": "http://schema.org/",
        "@type": "Corporation",
        name: entityName,
        tickerSymbol: entityTicker,
        location: {
          "@context": "http://schema.org/",
          "@type": "Place",
          address: entityCountry,
        },
        description: entityDescription,
        url: `${emberAppBaseUrl}/entities/${entitySlug}`,
        review: null,
      },
      keywords: keywords?.join(", "),
      about: [],
      url: canonicalUrl,
      genre: "news",
      inLanguage: "en-US",
      isPartOf: {
        "@type": ["CreativeWork", "Product"],
        name: "Smartkarma",
        productID: "smartkarma.com:subscribed",
      },
      isAccessibleForFree: isPublic ? "True" : "False",
      hasPart: [
        {
          "@type": "WebPageElement",
          isAccessibleForFree: "False",
          cssSelector: ".paywall-summary",
        },
        {
          "@type": "WebPageElement",
          isAccessibleForFree: "False",
          cssSelector: ".paywall-detail",
        },
      ],
    },
  ];
};

export const jsonLDForEntity = ({ entity, country, sector }: any) => {
  const entityName = entity?.shortName;
  const entitySlug = entity?.slug;
  const entityDescription = entity?.entityDescription;
  const entitySector = entity?.sector;
  const entityCountry = entity?.country;
  const entityTicker = entity?.uniqueTicker;

  const totalBullish = Math.max(entity?.totalBullish || 0, 0);
  const totalBearish = Math.max(entity?.totalBearish || 0, 0);
  const total = totalBullish + totalBearish;
  const ratingValue = 3 * (total ? totalBullish / total : 0) + 2;

  const items = [
    {
      "@type": "ListItem",
      item: {
        "@id": "https://www.smartkarma.com",
        name: "Smartkarma",
      },
    },
    ...(country
      ? [
          {
            "@type": "ListItem",
            item: {
              "@id": `https://www.smartkarma.com/countries/${country?.slug}`,
              name: entityCountry,
            },
          },
        ]
      : []),

    ...(entitySector && sector
      ? [
          {
            "@type": "ListItem",
            item: {
              "@id": `https://www.smartkarma.com/sectors/${sector?.slug}`,
              name: entitySector,
            },
          },
        ]
      : []),
    {
      "@type": "ListItem",
      item: {
        "@id": `https://www.smartkarma.com/entities/${entitySlug}`,
        name: shortSecurity(entityTicker),
      },
    },
    {
      "@type": "ListItem",
      item: {
        "@id": `https://www.smartkarma.com/entities/${entitySlug}`,
        name: entityName,
      },
    },
  ];

  return [
    {
      "@context": "http://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items.map((item, index) => ({
        ...item,
        position: index + 1,
      })),
    },
    {
      "@context": "http://schema.org/",
      "@type": "Corporation",
      name: entityName,
      tickerSymbol: entityTicker,
      location: {
        "@context": "http://schema.org/",
        "@type": "Place",
        address: entityCountry,
      },
      description: entityDescription,
      url: `https://www.smartkarma.com/entities/${entitySlug}`,
      review: {
        "@context": "http://schema.org/",
        "@type": "Review",
        author: {
          "@type": "Organization",
          name: "Smartkarma Insight Providers",
        },
        reviewRating: {
          "@type": "Rating",
          bestRating: 5,
          ratingValue,
          worstRating: 0,
        },
      },
    },
  ];
};

export const jsonLDForProfile = ({ profile }: any) => {
  const {
    isCompany,
    name,
    slug,
    title,
    summary,
    country,
    companyName,
    company,
  } = profile || {};

  const xlarge = profile?.avatarUrl?.xlarge;

  return [
    {
      "@context": "http://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          item: { "@id": "https://www.smartkarma.com", name: "Smartkarma" },
        },
        {
          "@type": "ListItem",
          position: 2,
          item: {
            "@id": "https://www.smartkarma.com/insight-provider-directory",
            name: "Insight Provider",
          },
        },
        {
          "@type": "ListItem",
          position: 3,
          item: {
            "@id": `https://www.smartkarma.com/${
              isCompany ? "companies" : "profiles"
            }/${slug}`,
            name,
          },
        },
      ],
    },
    ...(isCompany
      ? [
          {
            "@context": "http://schema.org/",
            "@type": "Organization",
            name,
            logo: {
              "@context": "http://schema.org/",
              "@type": "ImageObject",
              url: xlarge,
              caption: name,
            },
            description: null,
            address: country,
            sameAs: [],
            url: `https://www.smartkarma.com/companies/${slug}`,
            review: null,
          },
        ]
      : [
          {
            "@context": "http://schema.org/",
            "@type": "Person",
            name,
            jobTitle: title,
            description: summary,
            image: {
              "@context": "http://schema.org/",
              "@type": "ImageObject",
              url: xlarge,
              caption: name,
            },
            workLocation: {
              "@context": "http://schema.org/",
              "@type": "Place",
              address: country,
            },
            ...(company
              ? {
                  worksFor: {
                    "@context": "http://schema.org/",
                    "@type": "Organization",
                    name: companyName,
                    logo: {
                      "@context": "http://schema.org/",
                      "@type": "ImageObject",
                      url: company?.avatarUrl?.xlarge || xlarge,
                      caption: companyName,
                    },
                    description: null,
                    address: company?.country || country,
                    sameAs: [],
                    url: `https://www.smartkarma.com/companies/${company?.slug}`,
                    review: null,
                  },
                }
              : {}),
            sameAs: [],
            url: `https://www.smartkarma.com/profiles/${slug}`,
          },
        ]),
    {
      "@context": "http://schema.org",
      "@type": "ItemList",
      itemListElement: [
        // {
        //   "@type": "ListItem",
        //   position: 1,
        //   url: "https://www.smartkarma.com/insights/s-p500-rebalance-for-december-2021-rearranging-the-deckchairs",
        // },
        // {
        //   "@type": "ListItem",
        //   position: 2,
        //   url: "https://www.smartkarma.com/insights/keppel-thinking-about-keppel-smm-mergeco-and-new-keppel-pro-forma-opportunities",
        // },
        // {
        //   "@type": "ListItem",
        //   position: 3,
        //   url: "https://www.smartkarma.com/insights/sembcorp-marine-and-the-keppel-o-m-business-combination-brighter-light-at-the-end-of-the-tunnel",
        // },
        // {
        //   "@type": "ListItem",
        //   position: 4,
        //   url: "https://www.smartkarma.com/insights/tencent-s-big-in-specie-dividend-of-jd-com-shares",
        // },
        // {
        //   "@type": "ListItem",
        //   position: 5,
        //   url: "https://www.smartkarma.com/insights/hitachi-transport-9086-takeover-deal-imminent",
        // },
      ],
    },
  ];
};

export const jsonLDForCollection = ({ collection }: any) => {
  const { slug, name } = collection || {};

  return [
    {
      "@context": "http://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          item: { "@id": "https://www.smartkarma.com", name: "Smartkarma" },
        },
        {
          "@type": "ListItem",
          position: 2,
          item: {
            "@id": "https://www.smartkarma.com/insight-provider-directory",
            name: "Insight Provider",
          },
        },
        {
          "@type": "ListItem",
          position: 3,
          item: {
            "@id": `https://www.smartkarma.com/collections/${slug}`,
            name,
          },
        },
      ],
    },
  ];
};
